import React, { useState, useEffect, useRef, useId } from 'react';
import { useTranslation } from 'react-i18next';
import { CaptchaState, FormErrors } from '../../../../types/ContactForm';
import './CaptchaField.scss';

declare global {
  interface Window {
    grecaptcha?: {
      render: (container: string | HTMLElement, options: { sitekey: string; theme?: string; size?: string; callback?: () => void }) => number;
      getResponse: (widgetId?: number) => string;
      reset: (widgetId?: number) => void;
    };
    onRecaptchaLoad?: () => void;
    __contactFormRecaptchaOnLoad?: () => void;
    loadRecaptchaOnConsent?: () => void;
    CM?: { getSettings: (key: string) => boolean; openConfirmationModal: (key: string, cb: () => void) => void };
  }
}

interface CaptchaFieldProps {
  captcha: CaptchaState;
  errors: FormErrors;
  onCaptchaChange: (result: string) => void;
  captchaType?: string;
  apiUrl?: string;
  onRecaptchaReady?: (ready: boolean) => void;
  onRecaptchaWidgetId?: (widgetId: number) => void;
  sessionRecaptchaConsent?: boolean;
}

export const CaptchaField: React.FC<CaptchaFieldProps> = ({
  captcha,
  errors,
  onCaptchaChange,
  captchaType = 'math',
  apiUrl,
  onRecaptchaReady,
  onRecaptchaWidgetId,
  sessionRecaptchaConsent: sessionRecaptchaConsentFromParent
}) => {
  const { t } = useTranslation('form');
  const recaptchaContainerRef = useRef<HTMLDivElement>(null);
  const uniqueId = useId().replace(/:/g, '-');
  const recaptchaContainerId = `g-recaptcha-${uniqueId}`;
  const [captchaMarkup, setCaptchaMarkup] = useState<string>('');
  const [siteKey, setSiteKey] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleRecaptchaApiLoaded, setIsGoogleRecaptchaApiLoaded] = useState(false);
  const [consentUpdateTrigger, setConsentUpdateTrigger] = useState(0);
  const [sessionRecaptchaConsent, setSessionRecaptchaConsent] = useState(false);

  useEffect(() => {
    if (captchaType === 'google_recaptcha') {
      loadGoogleRecaptcha();
    }
  }, [captchaType]);

  // Single place: load script whenever we have consent (Once, Always, or parent session). Avoids races and double-load.
  useEffect(() => {
    if (captchaType !== 'google_recaptcha') return;
    const hasStored = typeof window.CM !== 'undefined' && window.CM.getSettings('recaptcha') === true;
    const hasConsent = hasStored || sessionRecaptchaConsent || sessionRecaptchaConsentFromParent === true;
    if (hasConsent) {
      loadGoogleRecaptchaScript();
    }
  }, [captchaType, sessionRecaptchaConsent, sessionRecaptchaConsentFromParent, consentUpdateTrigger]);

  useEffect(() => {
    if (captchaType !== 'google_recaptcha' || !siteKey || !isGoogleRecaptchaApiLoaded) {
      return;
    }
    const grecaptcha = window.grecaptcha;
    if (!grecaptcha || typeof grecaptcha.render !== 'function') {
      return;
    }
    const container = recaptchaContainerRef.current;
    if (!container || container.hasAttribute('data-widget-id')) {
      return;
    }

    const doRender = () => {
      const el = recaptchaContainerRef.current;
      if (!el || el.hasAttribute('data-widget-id') || !window.grecaptcha || typeof window.grecaptcha.render !== 'function') {
        return;
      }
      try {
        const widgetId = window.grecaptcha.render(el, {
          sitekey: siteKey,
          theme: 'light',
          size: 'normal',
          callback: () => {
            onRecaptchaReady?.(true);
          }
        });
        onRecaptchaWidgetId?.(widgetId);
        onRecaptchaReady?.(true);
      } catch (error) {
        console.error('Error rendering reCAPTCHA:', error);
      }
    };

    const id = requestAnimationFrame(() => {
      doRender();
    });
    return () => cancelAnimationFrame(id);
  }, [captchaType, siteKey, isGoogleRecaptchaApiLoaded, onRecaptchaReady, onRecaptchaWidgetId, consentUpdateTrigger]);

  const loadGoogleRecaptchaScript = () => {
    const RECAPTCHA_EXPLICIT_URL = 'https://www.google.com/recaptcha/api.js?onload=__contactFormRecaptchaOnLoad&render=explicit';
    const hasExplicitRender = () => typeof window.grecaptcha?.render === 'function';

    if (hasExplicitRender()) {
      setIsGoogleRecaptchaApiLoaded(true);
      return;
    }

    const existingScript = document.querySelector('script[src*="google.com/recaptcha/api.js"]');
    if (existingScript) {
      const existingSrc = (existingScript as HTMLScriptElement).src || '';
      if (existingSrc.includes('render=explicit')) {
        const checkReady = () => {
          if (hasExplicitRender()) {
            setIsGoogleRecaptchaApiLoaded(true);
          } else {
            setTimeout(checkReady, 100);
          }
        };
        checkReady();
        return;
      }
      existingScript.remove();
    }

    if (document.querySelector(`script[src*="${RECAPTCHA_EXPLICIT_URL.split('?')[0]}"]`)) {
      const checkReady = () => {
        if (hasExplicitRender()) {
          setIsGoogleRecaptchaApiLoaded(true);
        } else {
          setTimeout(checkReady, 100);
        }
      };
      checkReady();
      return;
    }

    window.__contactFormRecaptchaOnLoad = () => {
      if (hasExplicitRender()) {
        setIsGoogleRecaptchaApiLoaded(true);
      }
      delete window.__contactFormRecaptchaOnLoad;
    };

    const script = document.createElement('script');
    script.src = RECAPTCHA_EXPLICIT_URL;
    script.async = true;
    script.defer = true;
    script.onerror = () => {
      delete window.__contactFormRecaptchaOnLoad;
    };
    document.head.appendChild(script);
  };

  const loadRecaptchaOnConsent = () => {
    loadGoogleRecaptchaScript();
  };

  React.useEffect(() => {
    window.loadRecaptchaOnConsent = loadRecaptchaOnConsent;
    return () => {
      delete window.loadRecaptchaOnConsent;
    };
  }, []);

  useEffect(() => {
    const handleConsentUpdate = (e: Event) => {
      const customEvent = e as CustomEvent<{ recaptcha?: boolean }>;
      const detailRecaptcha = customEvent.detail?.recaptcha === true;
      if (detailRecaptcha) {
        setSessionRecaptchaConsent(true);
      }
      setConsentUpdateTrigger(prev => prev + 1);
    };

    document.addEventListener('consent.updated', handleConsentUpdate);
    return () => {
      document.removeEventListener('consent.updated', handleConsentUpdate);
    };
  }, []);

  const loadGoogleRecaptcha = async () => {
    if (!apiUrl) return;
    
    setIsLoading(true);
    try {
      const response = await fetch(`${apiUrl}contact-form/captcha?type=google_recaptcha`);
      const data = await response.json();
      
      if (data.data && data.data.markup) {
        setCaptchaMarkup(data.data.markup);
        
        const siteKeyMatch = data.data.markup.match(/data-sitekey="([^"]+)"/);
        if (siteKeyMatch) {
          setSiteKey(siteKeyMatch[1]);
        }
      }
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  const renderMathCaptcha = () => (
    <div className="captchaContainer">
      <span>{captcha.num1}</span>
      <span>+</span>
      <span>{captcha.num2}</span>
      <span>=</span>
      <input
        type="number"
        name="captcha"
        className="form-control"
        value={captcha.result}
        onChange={(e) => onCaptchaChange(e.target.value)}
        placeholder=" "
        required
        style={{ width: '80px', display: 'inline-block' }}
      />
    </div>
  );

  const renderGoogleRecaptcha = () => {
    if (isLoading) {
      return <div className="loading">Loading reCAPTCHA...</div>;
    }
    
    const hasStoredConsent = typeof window.CM !== 'undefined' && window.CM.getSettings('recaptcha') === true;
    const hasConsent = hasStoredConsent || sessionRecaptchaConsent || sessionRecaptchaConsentFromParent === true;
    
    if (!hasConsent) {
      return (
        <div className="recaptcha-consent-placeholder">
          <div style={{ 
            padding: '20px', 
            border: '1px dashed #ccc', 
            borderRadius: '4px', 
            textAlign: 'center',
            backgroundColor: '#f9f9f9'
          }}>
            <p style={{ color: '#666', marginTop: '0', marginBottom: '0' }}>
              {t('recaptchaConsentPlaceholder')}
            </p>
          </div>
        </div>
      );
    }
    
    if (captchaMarkup) {
      if (captchaMarkup.includes('alert-warning')) {
        return (
          <div 
            className="googleRecaptcha"
            dangerouslySetInnerHTML={{ __html: captchaMarkup }}
          />
        );
      }
      
      if (captchaMarkup.includes('g-recaptcha') && siteKey) {
        return (
          <div className="googleRecaptcha">
            <div
              ref={recaptchaContainerRef}
              id={recaptchaContainerId}
              className="g-recaptcha"
              data-sitekey={siteKey}
            />
          </div>
        );
      }
      
      return (
        <div className="error">
          <strong>reCAPTCHA konnte nicht geladen werden</strong><br/>
          <small>Bitte überprüfen Sie die Plugin-Konfiguration oder wählen Sie "Mathematisches Captcha" in den Einstellungen.</small>
        </div>
      );
    }
    
    return <div className="error">Failed to load reCAPTCHA</div>;
  };

  return (
    <div 
      id="form-group-captcha"
      aria-labelledby="form-group-label-captcha"
      className={`form-group ${errors.captcha ? 'has-error' : ''} zr-m-3`}
      role="group"
    >
      <div className="d-flex flex-column-reverse">
        <div className="captchaDisplay">
          {captchaType === 'math' ? renderMathCaptcha() : renderGoogleRecaptcha()}
        </div>
        
        <label 
          id="form-group-label-captcha"
          className="col-form-label pt-0"
        >
          Captcha <span className="text-danger">*</span>
        </label>
      </div>
      {errors.captcha && <div className="form-error-msg">{errors.captcha}</div>}
    </div>
  );
}; 